import {DateTime} from "luxon";
import {v4 as uuidv4} from 'uuid';

interface StudentInfo {
    id: string;
    firstName: string;
    lastName: string;
    gender: string;
    instrument: "piano" | "violin";
    grade: string; // 'pk', 'k', '1'-'12', 'a'
    course: string; // 'a', 'b', 'c', '1'-'60'
}

interface FamilyInfo {
    parentFirst: string;
    parentLast: string;

    regionID: string|number;
    defaultStudioID: string|number;

    students: StudentInfo[];
}

const SLOT_STATUS_UNAVAILABLE = "unavailable";
const SLOT_STATUS_AVAILABLE = "available";

interface ScheduleOpening {
    id: string|number;
    date: string;
    studioID: string|number;
    roomID: string|number;
    status: string;
    instruments: string[];
    grades: object;
    courses: object;
}

interface Studio {
    id: string|number;
    name: string;
}

interface ChosenTime {
    studentID: string|number;
    startDate: string;
    openingID: string|number;
}

interface BillingInfo {
    cardName: string;
    cardBillingAddress: string;
    cardBillingCity: string;
    cardBillingState: string;
    cardBillingZip: string;

    cardNumber: string;
    cardExp: string;
    cardCode: string;
}

interface ReserveSubmission {
    chosenTimes: ChosenTime[];
    billingInfo: BillingInfo|null;
}

interface SchedulingResult {
    errors: string[];
}

interface ScheduleAPI {
  fetchLinkData(token: string) : Promise<FamilyInfo>;
  fetchScheduleOpenings(): Promise<ScheduleOpening[]>;
  fetchStudios(regionID: string|number): Promise<Studio[]>;
  reserveScheduleTimes(submission: ReserveSubmission, token: string): Promise<SchedulingResult>;
}


const API_BASE_URL = process.env.VUE_APP_API_BASE_PATH;

class HproScheduleAPI {

    async fetchLinkData(token: string) : Promise<FamilyInfo> {
        const response = await fetch(API_BASE_URL + `/schedule_info?t=${token}`);

        return response.json();
    }

    async fetchScheduleOpenings(): Promise<ScheduleOpening[]> {
        const openings: ScheduleOpening[] = [];

        // fetch current openings
        // const startDate = DateTime.utc();
        // const endDate = startDate.plus({days: 21});
        //
        // const startDateFormatted = startDate.toISODate();
        // const endDateFormatted = endDate.toISODate();

        const response = await fetch(API_BASE_URL + `/openings`);

        return response.json();
    }

    async fetchStudios(regionID: string|number): Promise<Studio[]> {

        const response = await fetch(API_BASE_URL + `/studios?region_id=${regionID}`);
        return response.json();
    }

    async reserveScheduleTimes(reservation: ReserveSubmission, token: string) : Promise<SchedulingResult> {
        const data = {
            reservation,
            token
        }
        const response = await fetch(API_BASE_URL + `/schedule-opening`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            body: JSON.stringify(data),
            headers: {
             // 'Content-Type': 'application/json'
            },
        });

        if (response.status !== 200) {
            return new Promise((resolve) => {
                resolve({errors: ["There was a problem scheduling the lesson."]});
            });
        }

        return response.json();
    }
}


class FakeScheduleAPI {
    studios: Studio[];

    constructor() {
        this.studios = [
            {name: "Arden Hills", id: "1"},
            {name: "Eden Prairie", id: "2"},
        ]
    }
    async fetchLinkData(token: string) : Promise<FamilyInfo> {
        return new Promise((resolve) => {
            resolve({
                parentFirst: "Mom",
                    parentLast: "Mason",
                regionID: "1",
                defaultStudioID: "1",
                students: [
                {
                    id: "111",
                    firstName: "Bob",
                    lastName: "Bobson",
                    gender: "male",
                    instrument: "piano",
                    grade: "1",
                    course: 'a',
                },
                {
                    id: "222",
                    firstName: "Roberta",
                    lastName: "Bobson",
                    gender: "female",
                    instrument: "piano",
                    grade: "k",
                    course: 'a'
                }
            ]
            })
        })
    }

    async fetchScheduleOpenings(): Promise<ScheduleOpening[]> {
        const openings: ScheduleOpening[] = [];

        // generate 10 weekdays of openings
        const days = [];
        let d = DateTime.utc();
        for (let i  = 0; i < 10; i++) {
            days.push(d);
            d = d.plus({days: 1});
            while (d.weekday > 5) {
                d = d.plus({days: 1});
            }
        }

        let toggle = 0;

        for (const d of days) {
            for (const s of this.studios) {
                for (const r of [`${s.id}-1`, `${s.id}-2`,`${s.id}-3`]) {

                    // 3pm central
                    const startHour = 18;
                    let dt = d.set({hour: startHour, minute: 0});

                    for (let i = 0; i < 8; i++) {
                        dt = dt.plus({minutes: 30}).set({millisecond: 0, second: 0});
                        if ((i + parseInt(''+s.id) + dt.day) % 5 === 1) {
                            dt = dt.plus({minutes: 15});
                        }

                        openings.push({
                            id: uuidv4(),
                            roomID: r,
                            studioID: s.id,
                            date: dt.toISO({
                                suppressMilliseconds: true,
                            }),
                            status: ((dt.day + toggle) % 3 === 1) ? SLOT_STATUS_AVAILABLE: SLOT_STATUS_UNAVAILABLE,
                            instruments: ['piano'],
                            grades: {'piano': [null, null]},
                            courses: {'piano': [null, null]},
                        })

                        toggle += 1;
                    }

                }
            }
        }

        return new Promise((resolve, reject) => {
            resolve(openings);
        });
    }

    async fetchStudios(regionID: string|number): Promise<Studio[]> {
        return new Promise((resolve, reject) => {resolve(this.studios)});
    }
    reserveScheduleTimes(reservation: ReserveSubmission): Promise<SchedulingResult> {
        return new Promise((resolve) => {resolve({errors: ["There was a problem..."]})});
    }
}


class ScheduleLookup {
    openTimes: ScheduleOpening[];

    // Studio ID, ISO Date string, Room ID
    studioTimes: Map<string, Map<string, Map<string, ScheduleOpening[]>>>;


    _startDate: DateTime;

    constructor(openTimes: ScheduleOpening[]) {
        this.openTimes = openTimes;
        this.studioTimes = new Map();

        // Initialize start date to first lesson in list, but we will check it below for earliest date.
        if (openTimes.length > 0) {
            this._startDate = DateTime.fromISO(openTimes[0].date);
        } else {
            this._startDate = DateTime.now();
        }


        for (const openTime of openTimes) {
            const d = DateTime.fromISO(openTime.date);
            const isoDate = d.toISODate();

            // Update earliest date
            if (d < this._startDate) {
                this._startDate = d;
            }

            const studioID = "" + openTime.studioID;
            const roomID = "" + openTime.roomID;
            let studioDayLookup = this.studioTimes.get(studioID);
            if (!studioDayLookup) {
                studioDayLookup = new Map();
                this.studioTimes.set(studioID, studioDayLookup);
            }
            let studioDayRooms = studioDayLookup.get(isoDate);
            if (!studioDayRooms) {
                studioDayRooms = new Map();
                studioDayLookup.set(isoDate, studioDayRooms);
            }
            let times = studioDayRooms.get(roomID);
            if (!times) {
                times = [];
                studioDayRooms.set(roomID, times);
            }

            times.push(openTime);
        }
    }

    // Returns a map of RoomID => ScheduleOpening[] for a given studio and date.
    findOpenings(studioID: string, date: DateTime): Map<string, ScheduleOpening[]> {
        const studioTimes = this.studioTimes.get(studioID);
        if (!studioTimes) {
            return new Map();
        }
        const dayRooms = studioTimes.get(date.toISODate());
        if (!dayRooms) {
            return new Map();
        }
        return dayRooms;
    }

    getRooms(studioID: string, date: DateTime): IterableIterator<string> {
        const openings = this.findOpenings(studioID, date);
        return openings.keys();
    }

    getEarliestOpeningDate() {
        return this._startDate;
    }
}


export function getScheduleLookup(openTimes: ScheduleOpening[]) : ScheduleLookup {
    return new ScheduleLookup(openTimes);
}

export function formatLocalTime(dt_str: string): string {
  // Use user browser time
  let formatted = DateTime.fromISO(dt_str).toLocaleString({ hour: '2-digit', minute: '2-digit', hour12: true });
  if (formatted.startsWith('0')) {
    formatted = formatted.substr(1);
  }
  return formatted.replace(/(AM)|(PM)/, "")
}


export default function getScheduleAPI(): ScheduleAPI {
    return new HproScheduleAPI();
}